import _ from "lodash";

import type { Flag } from "@/models/flag";
import type Light from "@/models/light/light";
import { isNotCommissioned } from "@/services/light";

/**
 * cls global condition. it verify if all lights selected lights are cls before do the additional condition
 *
 * @param lights - collection of selected lights
 * @param additionalCondition - additional condition custom per flag
 * @returns Whether all lights are CLS and the additional condition passes.
 */
const clsCondition = (
    lights: Light[],
    additionalCondition: (lights: Light[]) => boolean = () => true,
): boolean => {
    const allCLS = _.every(lights, (light) => light.isCLS);
    return allCLS && additionalCondition(lights);
};

const flags: Flag[] = [
    {
        label: "Not LED",
        icon: "circle",
        color: "#363636",
        value: 1,
        group: "other",
        index: 1,
    },
    {
        label: "No VLC Signal",
        icon: "triangle",
        color: "#aaf0ff",
        value: 2,
        group: "critical",
        index: 2,
    },
    {
        label: "Full Outage",
        icon: "triangle",
        color: "#09907e",
        value: 4,
        group: "critical",
        index: 6,
    },
    {
        label: "Damaged",
        icon: "triangle",
        color: "#3a5068",
        value: 8,
        group: "critical",
        index: 3,
    },
    {
        label: "Duplicate VLC ID",
        icon: "circle",
        color: "#fc6f14",
        value: 16,
        group: "system",
        ignored: true,
        index: 8,
    },
    {
        label: "Duplicate BLE ID",
        icon: "triangle",
        color: "#b53939",
        value: 32,
        group: "system",
        index: 9,
    },
    {
        label: "Partial Outage",
        icon: "triangle",
        color: "#52e0cd",
        value: 64,
        group: "critical",
        index: 5,
    },
    {
        label: "No Primary BLE ID",
        icon: "circle",
        color: "#fb7daf",
        group: "system",
        value: 128,
        index: 10,
    },
    {
        label: "Highlighted",
        icon: "circle",
        color: "#b2ad92",
        value: 256,
        group: "other",
        index: 7,
    },
    {
        label: "Weak Antenna",
        icon: "triangle",
        color: "#9260ea",
        value: 512,
        group: "critical",
        index: 4,
    },
    {
        label: "Out of Scope",
        icon: "circle",
        color: "#f20505",
        value: 1024,
        group: "other",
        index: 11,
    },
    {
        label: "No BLE UUID",
        icon: "triangle",
        color: "#ff5095",
        value: 2048,
        group: "system",
        index: 12,
    },
    {
        label: "Missing Location Description",
        icon: "triangle",
        color: "#dd8139",
        group: "system",
        index: 12,
        value: (lights) =>
            clsCondition(lights, (l) =>
                _.every(l, (light) => !light.hasLocationDescription),
            ),
    },
    {
        label: "Emergency Light",
        icon: "circle",
        color: "#61bb46",
        group: "system",
        ignored: true,
        index: 13,
        value: clsCondition,
    },
    {
        label: "Not Commissioned",
        icon: "circle",
        color: "#9e9e9e",
        group: "system",
        index: 14,
        value: (lights) => _.every(lights, isNotCommissioned),
    },
];

export default {
    positioningTypes: ["VLC + BLE", "VLC", "BLE"],
    ceilingTypes: ["Tile(Grid)", "Open"],
    fixtureTypes: [
        { value: "ATL", label: "ATL" },
        { value: "BLT_contRow", label: "BLT_contRow" },
        { value: "BRUNO", label: "BRUNO" },
        { value: "CLX", label: "CLX" },
        { value: "ESL", label: "ESL" },
        { value: "IBG", label: "IBG" },
        { value: "LDN6", label: "LDN6" },
        { value: "Lightedge", label: "Lightedge" },
        { value: "MRTR", label: "MRTR" },
        { value: "MRTR8_1D", label: "MRTR8_1D" },
        { value: "MSX", label: "MSX" },
        { value: "RUBIK", label: "RUBIK" },
        { value: "SLOT_contRow", label: "SLOT_contRow" },
        { value: "TTZ", label: "TTZ" },
        { value: "Trilux_E_Line", label: "Trilux_E_Line" },
        { value: "Whisper", label: "Whisper" },
    ],
    tileSizes: {
        "2x4": { tileSizeX: 2, tileSizeY: 4 },
        "4x2": { tileSizeX: 4, tileSizeY: 2 },
        "2x2": { tileSizeX: 2, tileSizeY: 2 },
        "4x4": { tileSizeX: 4, tileSizeY: 4 },
    },
    flags,
    spatialReferenceCodes: [
        { label: "Web Mercator", value: "3857" },
        { label: "WGS 84", value: "4326" },
    ],
};
